import { useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import Button from '@pray/shared/components/ui/Button/Button';
import applePayLogo from '@pray/shared/images/apple-pay.svg';
import googlePayLogo from '@pray/shared/images/google-pay.svg';

import styles from './PaymentMethodWallet.module.scss';

export default function PaymentMethodWallet({
  label,
  amount,
  displayItems = [],
  requestPayerName = true,
  requestPayerEmail = true,
  disabled = false,
  onPaymentMethodLoad = null,
  onCheckoutReady = null,
  onCheckoutSuccess = null,
  onCheckoutFailure = null,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentRequestOptions = {
      country: 'US',
      currency: 'usd',
      total: {
        label,
        amount,
      },
      requestPayerName,
      requestPayerEmail,
      displayItems,
    };

    const stripePaymentRequest = stripe.paymentRequest(paymentRequestOptions);

    stripePaymentRequest.canMakePayment().then((result) => {
      onPaymentMethodLoad?.(result);
      if (result) {
        setPaymentRequest(stripePaymentRequest);
        if (result.applePay) {
          setPaymentMethod('apple-pay');
        } else if (result.googlePay) {
          setPaymentMethod('google-pay');
        }
      }
    });

    stripePaymentRequest.on('token', async (event) => {
      try {
        const recaptchaToken = await executeRecaptcha('checkout');
        await onCheckoutReady?.({ recaptchaToken, ...event });
        event.complete('success');
        onCheckoutSuccess?.();
      } catch (error) {
        event.complete('fail');
        onCheckoutFailure?.(error);
      }
    });
  }, [stripe, elements, paymentMethod]);

  if (!paymentRequest) return null;

  if (paymentMethod === 'apple-pay') {
    const eventName = 'Apple Pay button';
    return (
      <Button
        data-viewname={eventName}
        data-tapeventname={eventName}
        className={styles.paymentButton}
        disabled={disabled}
        onClick={() => paymentRequest.show()}
      >
        <img src={applePayLogo} alt="Apple Pay" />
      </Button>
    );
  }

  if (paymentMethod === 'google-pay') {
    const eventName = 'Google Pay button';
    return (
      <Button
        data-viewname={eventName}
        data-tapeventname={eventName}
        className={styles.paymentButton}
        disabled={disabled}
        onClick={() => paymentRequest.show()}
      >
        <img src={googlePayLogo} alt="Google Pay" />
      </Button>
    );
  }

  return null;
}
